/**
 * ENG-4341: Integration context rendering.
 *
 * Substitutes `{{context.foo}}` placeholders in a SKILL.md body with values
 * from the resolved plugin context, and appends a "## Team Overrides"
 * section if the freeform overrides text is non-empty.
 *
 * Substitution rules (matching the RFC §4 + §4b contracts):
 *   - String / boolean / number values are interpolated as-is.
 *   - Arrays and maps are JSON-stringified (no per-type formatter in the
 *     initial slice; skill authors can use JSON syntax in their prose).
 *   - A placeholder that references a missing key is replaced with the
 *     empty string and a warning is emitted via the warn callback. Never
 *     leave a literal {{context.foo}} in the rendered output.
 *
 * Lives in its own module so tests can import it without pulling in the
 * full manager-worker entry-point side effects.
 */

const PLUGIN_CONTEXT_PLACEHOLDER_RE = /\{\{\s*context\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;

const TEAM_OVERRIDES_HEADER =
  '## Team Overrides\n\n> **The following overrides anything you\'ve read above.** ' +
  'If any rule here conflicts with earlier instructions in this skill, follow what is ' +
  'written here. These are user-supplied directives that take precedence over the ' +
  'plugin\'s default guidance.\n';

function formatContextValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  // Arrays and objects: JSON-stringify so the agent sees a stable shape.
  // Skill authors who want a different format should describe the field
  // in their skill prose, not transform it here.
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

export function renderIntegrationSkillContent(
  raw: string,
  values: Record<string, unknown>,
  overrides: string,
  warn: (message: string) => void = () => {},
): string {
  // 1. Substitute {{context.<field>}} placeholders.
  const substituted = raw.replace(PLUGIN_CONTEXT_PLACEHOLDER_RE, (_match, fieldName: string) => {
    if (!(fieldName in values)) {
      warn(`unresolved placeholder {{context.${fieldName}}} — substituting empty string`);
      return '';
    }
    return formatContextValue(values[fieldName]);
  });

  // 2. Append the freeform overrides section if non-empty.
  const trimmedOverrides = overrides.trim();
  if (!trimmedOverrides) return substituted;

  const separator = substituted.endsWith('\n') ? '\n' : '\n\n';
  return `${substituted}${separator}---\n\n${TEAM_OVERRIDES_HEADER}\n${trimmedOverrides}\n`;
}
